import { useChangeDispositionMutation } from "@common/api"
import type { QuoteDispositionKind } from "@common/client"
import { handleError } from "@common/utils"
import useCustomToast from "@/hooks/use-custom-toast"

export const OPEN_DISPOSITIONS: QuoteDispositionKind[] = [
  "draft",
  "review",
  "customer_review",
  "customer_response",
]

const CLOSING_DISPOSITIONS: QuoteDispositionKind[] = [
  "won",
  "lost",
  "cancelled",
  "expired",
]

export const DISPOSITIONS = [
  "draft",
  "review",
  "customer_review",
  "customer_response",
  "won",
  "lost",
  "cancelled",
  "expired",
] as const satisfies readonly QuoteDispositionKind[]

export function dispositionTargets(
  disposition: QuoteDispositionKind,
): QuoteDispositionKind[] {
  const open = OPEN_DISPOSITIONS.includes(disposition)
  const closing = CLOSING_DISPOSITIONS.filter(
    (target) => target !== disposition && (open || target !== "won"),
  )
  return open ? closing : ["draft", ...closing]
}

export function useSelectDisposition(quoteId: string): {
  select: (disposition: QuoteDispositionKind) => void
  pending: boolean
} {
  const mutation = useChangeDispositionMutation()
  const showToast = useCustomToast()

  const select = (disposition: QuoteDispositionKind): void => {
    mutation.mutate(
      { quote_id: quoteId, quoteDispositionChange: { disposition } },
      {
        onSuccess: () =>
          showToast(
            "Status updated",
            `Quote marked as ${dispositionLabel(disposition)}`,
            "success",
          ),
        onError: (err) => handleError(err, showToast),
      },
    )
  }

  return { select, pending: mutation.isPending }
}

export function dispositionLabel(disposition: QuoteDispositionKind): string {
  switch (disposition) {
    case "draft":
      return "Draft"
    case "review":
      return "Internal review"
    case "customer_review":
      return "Customer review"
    case "customer_response":
      return "Customer response"
    case "won":
      return "Won"
    case "lost":
      return "Lost"
    case "cancelled":
      return "Canceled"
    case "expired":
      return "Expired"
  }
}
